import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import { Bar, Pie, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';

function Dashboard() {
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [confirmedDocuments, setConfirmedDocuments] = useState(0);
  const [rejectedDocuments, setRejectedDocuments] = useState(0);
  const [toReviewDocuments, setToReviewDocuments] = useState(0);
  const [documentsByCategory, setDocumentsByCategory] = useState({});
  const [totalPagesProcessed, setTotalPagesProcessed] = useState(0);
  const [averageProcessingTime, setAverageProcessingTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // New state variables
  const [uploadTrendsData, setUploadTrendsData] = useState(null);
  const [processingTimeTrendsData, setProcessingTimeTrendsData] = useState(null);
  const [processingTimeDistributionData, setProcessingTimeDistributionData] = useState(null);

  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!currentUser) return;

      try {
        setIsLoading(true);
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/documents`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Error fetching documents: ${response.statusText}`);
        }

        const data = await response.json();
        const storedDocuments = data.documents;

        // Process documents
        const total = storedDocuments.length;
        const confirmed = storedDocuments.filter(doc => doc.status === 'Confirmed').length;
        const rejected = storedDocuments.filter(doc => doc.status === 'Rejected').length;
        const toReview = storedDocuments.filter(doc => doc.status === 'To Review').length;

        setTotalDocuments(total);
        setConfirmedDocuments(confirmed);
        setRejectedDocuments(rejected);
        setToReviewDocuments(toReview);

        // Count documents by category
        const categoryCount = storedDocuments.reduce((acc, doc) => {
          const category = doc.category || 'Uncategorized';
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {});

        setDocumentsByCategory(categoryCount);

        // Calculate total pages processed and average processing time
        let totalPages = 0;
        let totalProcessingTime = 0;
        let processedDocumentsCount = 0;

        // For upload trends
        const uploadsPerDay = {};

        // For processing time trends
        const processingTimesPerDay = {};

        // For processing time distribution
        const processingTimes = [];

        storedDocuments.forEach(doc => {
          // Process upload dates
          if (doc.created_at) {
            const uploadDate = new Date(doc.created_at).toISOString().split('T')[0];
            uploadsPerDay[uploadDate] = (uploadsPerDay[uploadDate] || 0) + 1;
          }

          if (
            doc.processed_at &&
            doc.processing_started_at &&
            doc.num_pages
          ) {
            // Parse timestamps
            const processingStartedAt = new Date(doc.processing_started_at);
            const processedAt = new Date(doc.processed_at);
            const processingTime = (processedAt - processingStartedAt) / 1000; // in seconds

            if (processingTime > 0) {
              totalPages += doc.num_pages;
              totalProcessingTime += processingTime;
              processedDocumentsCount++;

              // For processing time trends
              const processingDate = processingStartedAt.toISOString().split('T')[0];
              if (!processingTimesPerDay[processingDate]) {
                processingTimesPerDay[processingDate] = { totalProcessingTime: 0, count: 0 };
              }
              processingTimesPerDay[processingDate].totalProcessingTime += processingTime;
              processingTimesPerDay[processingDate].count += 1;

              // For processing time distribution
              processingTimes.push(processingTime);
            }
          }
        });

        const avgProcessingTime = processedDocumentsCount > 0 ? totalProcessingTime / processedDocumentsCount : 0;

        setTotalPagesProcessed(totalPages);
        setAverageProcessingTime(avgProcessingTime);

        // Prepare data for upload trends chart
        const sortedUploadDates = Object.keys(uploadsPerDay).sort();
        const uploadCounts = sortedUploadDates.map(date => uploadsPerDay[date]);

        setUploadTrendsData({
          labels: sortedUploadDates,
          datasets: [
            {
              label: 'Documents Uploaded',
              data: uploadCounts,
              fill: false,
              backgroundColor: 'rgba(75,192,192,0.6)',
              borderColor: 'rgba(75,192,192,1)',
            },
          ],
        });

        // Prepare data for processing time trends chart
        const sortedProcessingDates = Object.keys(processingTimesPerDay).sort();
        const avgProcessingTimes = sortedProcessingDates.map(date => {
          const { totalProcessingTime, count } = processingTimesPerDay[date];
          return totalProcessingTime / count;
        });

        setProcessingTimeTrendsData({
          labels: sortedProcessingDates,
          datasets: [
            {
              label: 'Avg Processing Time (sec)',
              data: avgProcessingTimes,
              fill: false,
              backgroundColor: 'rgba(255,99,132,0.6)',
              borderColor: 'rgba(255,99,132,1)',
            },
          ],
        });

        // Prepare data for processing time distribution chart
        const binSize = 10;
        let binLabels = [];
        let binCounts = [];

        if (processingTimes.length > 0) {
          const maxTime = Math.ceil(Math.max(...processingTimes) / binSize) * binSize;
          const bins = Array.from({ length: Math.max(1, maxTime / binSize) }, (_, i) => ({
            binStart: i * binSize,
            binEnd: (i + 1) * binSize,
            count: 0,
          }));

          processingTimes.forEach(time => {
            const binIndex = Math.min(Math.floor(time / binSize), bins.length - 1);
            bins[binIndex].count += 1;
          });

          binLabels = bins.map(bin => `${bin.binStart}-${bin.binEnd} sec`);
          binCounts = bins.map(bin => bin.count);
        }

        setProcessingTimeDistributionData({
          labels: binLabels,
          datasets: [
            {
              label: 'Number of Documents',
              data: binCounts,
              backgroundColor: 'rgba(54, 162, 235, 0.6)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1,
            },
          ],
        });

      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [currentUser]);

  // Function to format seconds into "X minutes Y seconds"
  const formatProcessingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = (seconds % 60).toFixed(2);

    if (minutes > 0) {
      return `${minutes} min ${remainingSeconds} sec`;
    } else {
      return `${remainingSeconds} sec`;
    }
  };

  const data = {
    labels: ['Total', 'Confirmed', 'Rejected', 'To Review'],
    datasets: [
      {
        label: 'Document Status Overview',
        data: [totalDocuments, confirmedDocuments, rejectedDocuments, toReviewDocuments],
        backgroundColor: [
          'rgba(124, 58, 237, 0.6)',
          'rgba(34, 197, 94, 0.6)',
          'rgba(239, 68, 68, 0.6)',
          'rgba(234, 179, 8, 0.6)',
        ],
        borderColor: [
          'rgba(124, 58, 237, 1)',
          'rgba(34, 197, 94, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(234, 179, 8, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const categoryData = {
    labels: Object.keys(documentsByCategory),
    datasets: [
      {
        label: 'Documents by Category',
        data: Object.values(documentsByCategory),
        backgroundColor: [
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 99, 132, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  if (!currentUser) {
    return <div>Please log in to view your dashboard.</div>;
  }

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      <h1>Dashboard</h1>
      <div className="stats-container">
        <div className="stat-card">
          <h2>Total Documents</h2>
          <p>{totalDocuments}</p>
        </div>
        <div className="stat-card confirmed">
          <h2>Confirmed</h2>
          <p>{confirmedDocuments}</p>
        </div>
        <div className="stat-card rejected">
          <h2>Rejected</h2>
          <p>{rejectedDocuments}</p>
        </div>
        <div className="stat-card to-review">
          <h2>To Review</h2>
          <p>{toReviewDocuments}</p>
        </div>
        <div className="stat-card pages-processed">
          <h2>Total Pages Processed</h2>
          <p>{totalPagesProcessed}</p>
        </div>
        <div className="stat-card processing-time">
          <h2>Avg Processing Time</h2>
          <p>{formatProcessingTime(averageProcessingTime)}</p>
        </div>
      </div>
      <div className="chart-container">
        <Bar data={data} />
      </div>
      <div className="chart-container">
        <h2>Documents by Category</h2>
        <Pie data={categoryData} />
      </div>
      {uploadTrendsData && (
        <div className="chart-container">
          <h2>Document Upload Trends Over Time</h2>
          <Line data={uploadTrendsData} />
        </div>
      )}
      {processingTimeTrendsData && (
        <div className="chart-container">
          <h2>Average Processing Time Over Time</h2>
          <Line data={processingTimeTrendsData} />
        </div>
      )}
      {processingTimeDistributionData && (
        <div className="chart-container">
          <h2>Processing Time Distribution</h2>
          <Bar data={processingTimeDistributionData} />
        </div>
      )}
    </div>
  );
}

export default Dashboard;
